/**
 * Categorias de elementos exibidas na aba "Add > Elements" do editor.
 *
 * O catálogo devolvido por `GET v2/default-item` já vem agrupado, mas em
 * categorias de granularidade fina (Box, Box Hover, Accordion, Menu, ...).
 * Aqui essas categorias são reagrupadas nas categorias de produto, mantendo a
 * categoria original como segundo nível da navegação.
 *
 * `apiCategories` guarda o slug do título da categoria da API (mesma
 * normalização aplicada em `title_translated`). Uma categoria pode pertencer a
 * mais de um grupo: Accordion aparece em Menus e Boxes, Floating Button em
 * Buttons e Social Media.
 *
 * Os ícones ficam em `element-category-icons/{slug}.svg`.
 */

import TEXT_ICON from './element-category-icons/text.svg';
import IFRAME_ICON from './element-category-icons/iframe.svg';
import MENUS_ICON from './element-category-icons/menus.svg';
import BLOG_ICON from './element-category-icons/blog.svg';
import BUTTONS_ICON from './element-category-icons/buttons.svg';
import BUILDERALL_TOOLS_ICON from './element-category-icons/builderall-tools.svg';
import BOXES_ICON from './element-category-icons/boxes.svg';
import ANIMATIONS_ICON from './element-category-icons/animations.svg';
import FORMS_ICON from './element-category-icons/forms.svg';
import IMAGES_ICON from './element-category-icons/images.svg';
import VIDEOS_ICON from './element-category-icons/videos.svg';
import SOCIAL_MEDIA_ICON from './element-category-icons/social-media.svg';

export const MEMBERS_AREA_CATEGORY_ID = 79;

export const ELEMENT_CATEGORY_GROUPS = [
  {
    slug: 'text',
    icon: TEXT_ICON,
    translationKey: 'ct-element-text',
    label: 'Text',
    // Os presets de título/parágrafo são renderizados pela sidebar mesmo quando
    // o catálogo não devolve nenhum item de texto.
    alwaysVisible: true,
    apiCategories: ['text', 'texts', 'lists']
  },
  {
    slug: 'iframe',
    icon: IFRAME_ICON,
    translationKey: 'ct-element-iframe',
    label: 'Iframe',
    apiCategories: ['iframe']
  },
  {
    slug: 'menus',
    icon: MENUS_ICON,
    translationKey: 'ct-element-menus',
    label: 'Menus',
    apiCategories: ['big-menu', 'menu', 'menu-dripping', 'accordion']
  },
  {
    slug: 'blog',
    icon: BLOG_ICON,
    translationKey: 'ct-element-blog',
    label: 'Blog',
    apiCategories: [
      'blog-featured',
      'blog-feednews',
      'blog-general-apps',
      'blog-group-news',
      'blog-lists',
      'blog-post'
    ]
  },
  {
    slug: 'buttons',
    icon: BUTTONS_ICON,
    translationKey: 'ct-element-buttons',
    label: 'Buttons',
    apiCategories: ['button', 'floating-button', 'btn-user-2']
  },
  {
    slug: 'builderall-tools',
    icon: BUILDERALL_TOOLS_ICON,
    translationKey: 'ct-element-builderall-tools',
    label: 'Builderall Tools',
    apiCategories: ['booking', 'checkout', 'quiz', 'user-items']
  },
  {
    slug: 'boxes',
    icon: BOXES_ICON,
    translationKey: 'ct-element-boxes',
    label: 'Boxes',
    apiCategories: ['box', 'box-hover', 'accordion']
  },
  {
    slug: 'animations',
    icon: ANIMATIONS_ICON,
    translationKey: 'ct-element-animations',
    label: 'Animations',
    apiCategories: ['timer', 'animated-elements', 'lottie-file']
  },
  {
    slug: 'forms',
    icon: FORMS_ICON,
    translationKey: 'ct-element-forms',
    label: 'Forms',
    apiCategories: [
      'email-marketing-form',
      'get-response',
      'multistepformcomponent',
      'magic-form',
      'contact-form'
    ]
  },
  {
    slug: 'images',
    icon: IMAGES_ICON,
    translationKey: 'ct-element-images',
    label: 'Images',
    apiCategories: ['galleries', 'image', 'images', 'lines', 'rotation-banner']
  },
  {
    slug: 'videos',
    icon: VIDEOS_ICON,
    translationKey: 'ct-element-videos',
    label: 'Videos',
    apiCategories: ['video', 'video-gallery', 'webinar']
  },
  {
    slug: 'social-media',
    icon: SOCIAL_MEDIA_ICON,
    translationKey: 'ct-element-social-media',
    label: 'Social Media',
    apiCategories: ['instagram-feed', 'social-bars', 'floating-button']
  }
];

/**
 * Categorias criadas no admin depois desta lista caem aqui: cada item é
 * direcionado pelo nome do componente Vue. A ordem importa — o primeiro padrão
 * que casar vence (VideoGallery vai para vídeos, CounterText para animações).
 */
const COMPONENT_NAME_FALLBACKS = [
  { slug: 'blog', pattern: /^Blog/ },
  { slug: 'videos', pattern: /(Video|Youtube|Vimeo|Panda|Wistia|Vidello|Webinar)/ },
  { slug: 'forms', pattern: /(Form|LaunchApp)/ },
  { slug: 'animations', pattern: /(Timer|Lottie|Counter)/ },
  { slug: 'social-media', pattern: /(Instagram|Social)/ },
  { slug: 'menus', pattern: /(Menu|Accordion|ContentNavUser)/ },
  { slug: 'buttons', pattern: /Button/ },
  { slug: 'images', pattern: /(Image|Gallery|Slider|Line)/ },
  { slug: 'boxes', pattern: /Box/ },
  { slug: 'iframe', pattern: /Iframe/ },
  { slug: 'text', pattern: /Text/ },
  { slug: 'builderall-tools', pattern: /(Booking|Checkout|Quiz)/ }
];

const DEFAULT_GROUP_SLUG = 'builderall-tools';

const GROUPS_BY_API_CATEGORY = ELEMENT_CATEGORY_GROUPS.reduce((map, group) => {
  group.apiCategories.forEach((categorySlug) => {
    map[categorySlug] = map[categorySlug] || [];
    map[categorySlug].push(group.slug);
  });

  return map;
}, {});

export const slugifyCategoryTitle = (title) => {
  return String(title || '')
    .split('.')
    .join('-')
    .replace(/\s+/g, '-')
    .toLowerCase();
};

const groupSlugsForComponent = (componentName) => {
  const name = String(componentName || '');
  const match = COMPONENT_NAME_FALLBACKS.find((fallback) => fallback.pattern.test(name));

  return [match ? match.slug : DEFAULT_GROUP_SLUG];
};

const isVisibleItem = (item, showMembersItems) => {
  if (!item || item.show === 0 || !item.thumbnail)
    return false;


  if (!showMembersItems && parseInt(item.category_id, 10) === MEMBERS_AREA_CATEGORY_ID)
    return false;


  return true;
};

/**
 * Distribui os itens das categorias da API nas categorias da sidebar.
 *
 * @param {Array} categories categorias `type=app` devolvidas por `GET v2/default-item`
 * @param {Object} options `showMembersItems` libera os itens de área de membros
 * @returns {Array} grupos na ordem do produto, cada um com `categories`
 */
export const groupApiCategories = (categories, options = {}) => {
  const showMembersItems = options.showMembersItems === true;
  const buckets = {};

  ELEMENT_CATEGORY_GROUPS.forEach((group) => {
    buckets[group.slug] = {};
  });

  (categories || []).forEach((category) => {
    if (!category || category.show === 0 || !Array.isArray(category.items_active))
      return;


    const categorySlug = category.title_translated || slugifyCategoryTitle(category.title);
    const mappedGroups = GROUPS_BY_API_CATEGORY[categorySlug];
    const visibleItems = category.items_active.filter((item) => {
      return isVisibleItem(item, showMembersItems);
    });

    if (!visibleItems.length)
      return;


    const itemsByGroup = {};

    visibleItems.forEach((item) => {
      const targetGroups = mappedGroups || groupSlugsForComponent(item.name);

      targetGroups.forEach((groupSlug) => {
        itemsByGroup[groupSlug] = itemsByGroup[groupSlug] || [];
        itemsByGroup[groupSlug].push(item);
      });
    });

    Object.keys(itemsByGroup).forEach((groupSlug) => {
      buckets[groupSlug][categorySlug] = {
        id: category.id,
        slug: categorySlug,
        title: category.title,
        translationKey: 'ct-element-' + categorySlug,
        tutorialLink: category.tutorial_link,
        items: itemsByGroup[groupSlug]
      };
    });
  });

  return ELEMENT_CATEGORY_GROUPS.map((group) => {
    const categorySlugs = Object.keys(buckets[group.slug]);

    categorySlugs.sort((firstSlug, secondSlug) => {
      const firstIndex = group.apiCategories.indexOf(firstSlug);
      const secondIndex = group.apiCategories.indexOf(secondSlug);
      const firstOrder = firstIndex === -1 ? group.apiCategories.length : firstIndex;
      const secondOrder = secondIndex === -1 ? group.apiCategories.length : secondIndex;

      return firstOrder - secondOrder;
    });

    return Object.assign({}, group, {
      categories: categorySlugs.map((categorySlug) => {
        return buckets[group.slug][categorySlug];
      })
    });
  });
};



// WEBPACK FOOTER //
// ./src/js/config/elementCategoryGroups.js